import React, { useState } from 'react';
import { BucketType, DocumentSummary } from '../../../types/chat';
import { Avatar, Box, Button, Chip, CircularProgress, Divider, Grid2, Stack, Tooltip, Typography } from '@mui/material';
import { callAPISync, getAllParticipants } from '../../../utils/api';
import { useLogto } from '@logto/react';
import { Autorenew, PriorityHigh } from '@mui/icons-material';
import { SnackbarAlert } from '../../SnackbarAlert';

interface SummaryProps extends DocumentSummary {
	pipelineType: string;
	caseRef: string;
	messagesOnPage: number;
	removedDuplicates?: number;
	hasSystemMessages?: boolean;
}

const PageSummary: React.FC<SummaryProps> = ({
	chatId,
	originalFileName,
	source,
	participantCount,
	participants,
	messagesInThread,
	messagesOnPage,
	pipelineType,
	caseRef,
	removedDuplicates,
	hasSystemMessages,
}) => {
	const logto = useLogto();
	const [currentParticipants, updateParticipants] = useState<BucketType[]>(participants);
	const [fetchingParticipants, setFetchingParticipants] = useState<boolean>(false);
	const [participantError, setParticipantError] = useState<string>('');

	let chatType = 'Group';
	if ((hasSystemMessages && participantCount < 4) || (!hasSystemMessages && participantCount < 3)) {
		chatType = 'Direct Message';
	}

	const moreParticipants = participantCount - currentParticipants.length;

	const fetchParticipants = () => {
		setParticipantError('');
		setFetchingParticipants(true);

		callAPISync(logto, (token) => {
			try {
				getAllParticipants(token, caseRef, chatId, pipelineType)
					.then((res) => {
						if (res.ok) {
							return res.json() as Promise<BucketType[]>;
						} else {
							return [];
						}
					})
					.then((participantJSON) => {
						if (participantJSON.length > 0) {
							updateParticipants(participantJSON);
							setFetchingParticipants(false);
						} else {
							setParticipantError('Did not receive valid list of participants from API');
							setFetchingParticipants(false);
						}
					})
					.catch((e) => {
						setParticipantError(String(e));
						setFetchingParticipants(false);
					});
			} catch (e) {
				setParticipantError(String(e));
				setFetchingParticipants(false);
			}
		});
	};

	const getLoadMoreChip = () => {
		if (fetchingParticipants) {
			return (
				<Button
					variant="outlined"
					startIcon={<CircularProgress size={'18px'} color="inherit" />}
					sx={{ textTransform: 'none', borderRadius: 20 }}
					disabled
				>
					Loading
				</Button>
			);
		} else if (participantError) {
			return (
				<Tooltip title={participantError}>
					<Button
						variant="outlined"
						onClick={() => {
							fetchParticipants();
						}}
						startIcon={<PriorityHigh />}
						sx={{ borderRadius: 20, textTransform: 'none' }}
						color={'error'}
					>
						Error
					</Button>
				</Tooltip>
			);
		} else {
			return (
				<Button
					variant="outlined"
					data-testid="load-more-participants-button"
					onClick={() => {
						fetchParticipants();
					}}
					startIcon={<Autorenew />}
					sx={{ borderRadius: 20, textTransform: 'none' }}
					color="primary"
				>
					{`Load ${moreParticipants} More`}
				</Button>
			);
		}
	};

	return (
		<Stack
			margin={'auto'}
			width={'80%'}
			padding={1}
			marginY={2}
			textAlign={'center'}
			borderRadius={2}
			sx={{ backgroundColor: 'background.paper', whiteSpace: 'pre-line' }}
			data-testid={`page-summary-container`}
			spacing={1}
		>
			<Typography fontWeight={'bold'}>Thread Overview</Typography>
			<Divider />

			<Box>
				<Typography>{`Original File Name: ${originalFileName}`}</Typography>
				<Typography>{`Source Application: ${source}`}</Typography>
				<Typography>{`Chat Type: ${chatType}`}</Typography>
			</Box>

			<Box justifyItems={'center'}>
				<Typography>{`${moreParticipants > 0 ? `Top ` : ''}${participants.length} Participants in Thread by Message Count:`}</Typography>
				<Grid2 container direction={'row'} spacing={1} justifyContent={'center'} maxWidth={'100%'} flexWrap={'wrap'}>
					{currentParticipants.map((participant) => {
						const numMessages = participant.doc_count ? participant.doc_count : 0;
						return (
							<>
								<Chip
									key={`participant-text-${participant.key}`}
									data-testid={`${participant.key}-participant-chip`}
									label={participant.key}
									avatar={
										<Tooltip title={numMessages > 999 ? `${numMessages} total messages` : ''}>
											<Avatar sx={{ backgroundColor: 'background.default' }}>{numMessages > 999 ? '1k+' : numMessages}</Avatar>
										</Tooltip>
									}
								/>
							</>
						);
					})}
				</Grid2>
				<Box sx={{ paddingTop: 1 }}>{moreParticipants > 0 && getLoadMoreChip()}</Box>
			</Box>

			<Box>
				<Typography>{`Messages in thread: ${messagesInThread}`}</Typography>
				<Typography>{`Messages on page: ${messagesOnPage}`}</Typography>
				{Boolean(removedDuplicates) && <Typography>{`Duplicates removed: ${removedDuplicates}`}</Typography>}
			</Box>

			<SnackbarAlert
				severity="error"
				open={Boolean(participantError)}
				alertMessage={participantError}
				onClose={() => setParticipantError('')}
			/>
		</Stack>
	);
};

export default PageSummary;
